import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

const RIGHT_KEYS = ["fld_view", "fld_entry", "fld_edit", "fld_delete"];

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function SentinelPermissionModules() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);

  const [memberId, setMemberId] = useState(params.get("member_id") || "");
  const [searchMemberId, setSearchMemberId] = useState(params.get("member_id") || "");

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [modules, setModules] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchInitialData(local, token);
  }, [navigate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const fetchInitialData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, modulesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/sentinel_rights/modules`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!modulesRes.ok) throw new Error("Failed to fetch modules");

      const dashboardResult = await dashboardRes.json();
      const modulesResult = await modulesRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const moduleRows = modulesResult?.data || [];
      setModules(moduleRows);

      if (memberId) {
        await fetchPermissions(memberId, token, moduleRows);
      } else {
        const blank = buildBlankPermissions(moduleRows);
        setPermissions(blank);
        setOriginalPermissions(JSON.parse(JSON.stringify(blank)));
      }
    } catch (error) {
      console.error("Sentinel permission page fetch error:", error);
      setErrorMessage(error.message || "Failed to load permission page.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async (id, tokenArg, moduleArg) => {
    try {
      setErrorMessage("");
      setSuccessMessage("");

      const token = tokenArg || localStorage.getItem("token") || localStorage.getItem("authToken");
      const moduleRows = moduleArg || modules;

      const res = await fetch(`${API_BASE_URL}/sentinel_rights/${encodeURIComponent(id)}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch permissions");
      }

      const grouped = mergeModulesWithSavedRights(moduleRows, data.data || []);

      setPermissions(grouped);
      setOriginalPermissions(JSON.parse(JSON.stringify(grouped)));
    } catch (error) {
      console.error("Sentinel permission fetch error:", error);

      const blank = buildBlankPermissions(modules);
      setPermissions(blank);
      setOriginalPermissions(JSON.parse(JSON.stringify(blank)));
      setErrorMessage(error.message || "Failed to fetch permissions.");
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchMemberId.trim()) {
      setErrorMessage("Please enter member ID.");
      return;
    }

    const id = searchMemberId.trim();
    setMemberId(id);

    window.history.pushState({}, "", `?member_id=${encodeURIComponent(id)}`);

    setLoading(true);
    await fetchPermissions(id);
    setLoading(false);
  };

  const updatePermission = (moduleName, index, key, value) => {
    setPermissions((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[moduleName][index][key] = value;
      return copy;
    });
  };

  const toggleAllInModule = (moduleName, checked) => {
    setPermissions((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));

      copy[moduleName] = copy[moduleName].map((row) => ({
        ...row,
        fld_view: checked,
        fld_entry: checked,
        fld_edit: checked,
        fld_delete: checked,
      }));

      return copy;
    });
  };

  const handleReset = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
    setSuccessMessage("Permissions reset successfully.");
  };

  const handleSave = async () => {
    try {
      if (!memberId.trim()) {
        setErrorMessage("Please enter member ID before saving.");
        return;
      }

      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const rights = flattenPermissions(permissions);

      const res = await fetch(
        `${API_BASE_URL}/sentinel_rights/${encodeURIComponent(memberId)}/save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            rights,
            created_by_admin_id: Number(localStorage.getItem("admin_id") || 1),
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save permissions");
      }

      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
      setSuccessMessage("Sentinel permissions saved successfully!");
      setTimeout(() => navigate("/sentinel_user_list"), 1000);
    } catch (error) {
      console.error("Sentinel permission save error:", error);
      setErrorMessage(error.message || "Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    let totalPages = 0;
    let activePermissions = 0;

    Object.values(permissions).forEach((rows) => {
      totalPages += rows.length;

      rows.forEach((row) => {
        RIGHT_KEYS.forEach((key) => {
          if (row[key]) activePermissions++;
        });
      });
    });

    return {
      totalModules: Object.keys(permissions).length,
      totalPages,
      activePermissions,
      totalSlots: totalPages * 4,
    };
  }, [permissions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel permissions...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel_permission_modules"
      activeMenu="users"
    >
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel Permission Modules
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Manage module access permissions for sentinel guards
        </p>

        {successMessage && (
          <div className="flex items-center gap-2.5 mb-3.5 px-4 py-3 rounded-xl text-[13px] font-bold bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="flex items-center gap-2.5 mb-3.5 px-4 py-3 rounded-xl text-[13px] font-bold bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]">
            {errorMessage}
          </div>
        )}

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Sentinel User Permission Control
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Update view, entry, edit and delete access
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/sentinel_user_list")}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Back to User List
            </button>
          </div>
        </div>

        {/* Search Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              Search Sentinel Guard
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Enter member ID to load permissions
            </p>
          </div>
          <form onSubmit={handleSearch} className="flex gap-3 flex-wrap">
            <input
              value={searchMemberId}
              onChange={(e) => setSearchMemberId(e.target.value)}
              placeholder="Enter Member ID. Example: MEM008"
              className="w-full md:w-80 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />
            <button
              type="submit"
              className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Load Permissions
            </button>
          </form>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0 mb-1">
              Member ID
            </p>
            <h3 className="text-[24px] font-extrabold text-[var(--accent)] m-0">
              {memberId || "-"}
            </h3>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0 mb-1">
              Total Modules
            </p>
            <h3 className="text-[24px] font-extrabold text-[var(--accent)] m-0">
              {stats.totalModules}
            </h3>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0 mb-1">
              Total Pages
            </p>
            <h3 className="text-[24px] font-extrabold text-[var(--accent)] m-0">
              {stats.totalPages}
            </h3>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0 mb-1">
              Active Permissions
            </p>
            <h3 className="text-[24px] font-extrabold text-[var(--accent)] m-0">
              {stats.activePermissions} / {stats.totalSlots}
            </h3>
          </div>
        </div>

        {/* Permissions Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              Sentinel Page Rights
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Enable or disable View, Entry, Edit and Delete permissions
            </p>
          </div>

          {Object.keys(permissions).length === 0 ? (
            <div className="py-4 px-4 rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] font-semibold text-center">
              No modules found.
            </div>
          ) : (
            <div className="space-y-3.5">
              {Object.entries(permissions).map(([moduleName, rows]) => (
                <PermissionModuleCard
                  key={moduleName}
                  moduleName={moduleName}
                  rows={rows}
                  onChange={updatePermission}
                  onToggleAll={toggleAllInModule}
                />
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 flex-wrap mt-1.5 mb-3">
          <button
            type="button"
            onClick={() => navigate("/sentinel_user_list")}
            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
          >
            Back to User List
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={saving || Object.keys(permissions).length === 0}
            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || Object.keys(permissions).length === 0}
            className="border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            style={{
              background: "var(--btn-bg)",
              color: "var(--btn-text)",
              boxShadow: "0 4px 18px var(--accent-glow)",
            }}
          >
            {saving ? "Saving..." : "Save Permissions"}
          </button>
        </div>
      </div>
    </Layout>
  );
}

function buildBlankPermissions(modules = []) {
  const grouped = {};

  modules.forEach((mod) => {
    const moduleName = mod.module_name || "Unknown Module";

    grouped[moduleName] = [];

    grouped[moduleName].push({
      page_id: mod.page_id,
      subpage_id: null,
      fld_department: moduleName,
      fld_submodules: "",
      fld_view: false,
      fld_entry: false,
      fld_edit: false,
      fld_delete: false,
    });

    (mod.submodules || []).forEach((sub) => {
      grouped[moduleName].push({
        page_id: mod.page_id,
        subpage_id: sub.subpage_id,
        fld_department: moduleName,
        fld_submodules: sub.submodule_name || "",
        fld_view: false,
        fld_entry: false,
        fld_edit: false,
        fld_delete: false,
      });
    });
  });

  return grouped;
}

function mergeModulesWithSavedRights(modules = [], saved = []) {
  const savedMap = {};

  saved.forEach((row) => {
    const key = `${Number(row.page_id)}_${row.subpage_id ? Number(row.subpage_id) : "main"}`;
    savedMap[key] = row;
  });

  const grouped = {};

  modules.forEach((mod) => {
    const moduleName = mod.module_name || "Unknown Module";

    grouped[moduleName] = [];

    const mainKey = `${Number(mod.page_id)}_main`;
    const mainSaved = savedMap[mainKey];

    grouped[moduleName].push({
      page_id: mod.page_id,
      subpage_id: null,
      fld_department: moduleName,
      fld_submodules: "",
      fld_view: toBoolean(mainSaved?.fld_view),
      fld_entry: toBoolean(mainSaved?.fld_entry),
      fld_edit: toBoolean(mainSaved?.fld_edit),
      fld_delete: toBoolean(mainSaved?.fld_delete),
    });

    (mod.submodules || []).forEach((sub) => {
      const key = `${Number(mod.page_id)}_${Number(sub.subpage_id)}`;
      const subSaved = savedMap[key];

      grouped[moduleName].push({
        page_id: mod.page_id,
        subpage_id: sub.subpage_id,
        fld_department: moduleName,
        fld_submodules: sub.submodule_name || "",
        fld_view: toBoolean(subSaved?.fld_view),
        fld_entry: toBoolean(subSaved?.fld_entry),
        fld_edit: toBoolean(subSaved?.fld_edit),
        fld_delete: toBoolean(subSaved?.fld_delete),
      });
    });
  });

  return grouped;
}

function flattenPermissions(permissions = {}) {
  const rows = [];

  Object.values(permissions).forEach((moduleRows) => {
    moduleRows.forEach((row) => {
      rows.push({
        page_id: Number(row.page_id),
        subpage_id:
          row.subpage_id === null || row.subpage_id === undefined || row.subpage_id === ""
            ? null
            : Number(row.subpage_id),
        fld_department: row.fld_department || "",
        fld_submodules: row.fld_submodules || null,
        fld_view: !!row.fld_view,
        fld_entry: !!row.fld_entry,
        fld_edit: !!row.fld_edit,
        fld_delete: !!row.fld_delete,
      });
    });
  });

  return rows;
}

function toBoolean(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "true" ||
    String(value).toUpperCase() === "YES"
  );
}

function PermissionModuleCard({ moduleName, rows, onChange, onToggleAll }) {
  const skin = useSkin();

  return (
    <div className="border border-[var(--surface-border)] rounded-2xl overflow-hidden bg-[var(--card-bg)]">
      <div
        className="px-5 py-4 flex justify-between items-center gap-3 flex-wrap"
        style={{ background: skin.gradient }}
      >
        <div className="text-[17px] font-extrabold text-white">
          {moduleName}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onToggleAll(moduleName, true)}
            className="px-3 py-1.5 rounded-xl bg-white/20 text-white text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-white/30"
          >
            Enable All
          </button>
          <button
            type="button"
            onClick={() => onToggleAll(moduleName, false)}
            className="px-3 py-1.5 rounded-xl bg-white/20 text-white text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-white/30"
          >
            Disable All
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[13px] font-['Inter',system-ui,sans-serif]">
          <thead className="bg-[var(--surface)] text-[var(--text-sub)] border-b border-[var(--divider)]">
            <tr>
              <th className="text-left px-4 py-3 font-extrabold uppercase text-[11px] tracking-wider">
                Page / Sub Module
              </th>
              {["View", "Entry", "Edit", "Delete"].map((label) => (
                <th key={label} className="text-center px-4 py-3 font-extrabold uppercase text-[11px] tracking-wider">
                  {label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${row.page_id}-${row.subpage_id || "main"}`}
                className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)]"
              >
                <td className="px-4 py-3 text-[var(--text-primary)] font-semibold">
                  {row.subpage_id ? row.fld_submodules : "Main Module"}
                </td>

                {RIGHT_KEYS.map((key) => (
                  <td key={key} className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={!!row[key]}
                      onChange={(e) => onChange(moduleName, index, key, e.target.checked)}
                      className="w-4 h-4 accent-[var(--accent)] cursor-pointer"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}